class CustomerReviewsSlider extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.customer-reviews__list');
    this.progressFill = this.querySelector('.customer-reviews__progress-fill');
    this.nextButton = this.querySelector('[data-scroll-next]');
    this.prevButton = this.querySelector('[data-scroll-prev]');
    this.currentIndex = 0;
    this.isNavigating = false;
    this.pendingTarget = 0;

    if (!this.list) return;

    this.onScroll = this.handleScroll.bind(this);
    this.onScrollEnd = this.handleScrollEnd.bind(this);

    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    this.list.addEventListener('scrollend', this.onScrollEnd);

    this.resizeObserver = new ResizeObserver(() => {
      this.syncCurrentIndex();
      this.handleScroll();
    });
    this.resizeObserver.observe(this.list);

    this.prevButton?.addEventListener('click', () => this.scrollByDirection(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByDirection(1));

    requestAnimationFrame(() => {
      this.syncCurrentIndex();
      this.handleScroll();
    });
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.onScroll);
    this.list?.removeEventListener('scrollend', this.onScrollEnd);
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.navTimer);
  }

  getItems() {
    return [...this.list.querySelectorAll('.customer-reviews__item')];
  }

  getMaxScroll() {
    return Math.max(0, this.list.scrollWidth - this.list.clientWidth);
  }

  getAlignedIndex(items = this.getItems()) {
    const anchor = this.list.getBoundingClientRect().left;
    let activeIndex = 0;
    let smallestDistance = Infinity;

    items.forEach((item, index) => {
      const distance = Math.abs(item.getBoundingClientRect().left - anchor);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  getScrollPosition(index, items = this.getItems()) {
    const item = items[index];
    if (!item) return 0;

    const position = Math.round(item.getBoundingClientRect().left - this.list.getBoundingClientRect().left + this.list.scrollLeft);
    return Math.min(Math.max(0, position), this.getMaxScroll());
  }

  syncCurrentIndex() {
    const items = this.getItems();
    if (!items.length) return;

    if (this.list.scrollLeft <= 1) {
      this.currentIndex = 0;
      return;
    }

    this.currentIndex = this.getAlignedIndex(items);
  }

  canScrollPrev(items = this.getItems()) {
    if (!items.length) return false;
    if (this.currentIndex > 0) return true;

    const alignedPosition = this.getScrollPosition(0, items);
    return this.list.scrollLeft > alignedPosition + 1;
  }

  canScrollNext(items = this.getItems()) {
    if (!items.length) return false;

    const maxScroll = this.getMaxScroll();
    if (this.list.scrollLeft >= maxScroll - 1) return false;

    return this.currentIndex < items.length - 1;
  }

  handleScroll() {
    this.updateProgress();
    if (!this.isNavigating) {
      this.updateNavState();
    }
  }

  handleScrollEnd() {
    if (!this.isNavigating) {
      this.syncCurrentIndex();
      this.updateNavState();
      return;
    }

    window.clearTimeout(this.navTimer);
    this.finishNavigation(this.pendingTarget);
  }

  updateProgress() {
    if (!this.list || !this.progressFill) return;

    const maxScroll = this.getMaxScroll();

    if (maxScroll <= 0) {
      this.progressFill.style.width = '100%';
      return;
    }

    const progress = ((this.list.scrollLeft + this.list.clientWidth) / this.list.scrollWidth) * 100;
    this.progressFill.style.width = `${Math.min(100, Math.max(0, progress))}%`;
  }

  updateNavState() {
    const items = this.getItems();

    this.prevButton?.toggleAttribute('disabled', !this.canScrollPrev(items));
    this.nextButton?.toggleAttribute('disabled', !this.canScrollNext(items));
  }

  finishNavigation(target) {
    if (!this.isNavigating) return;

    window.clearTimeout(this.navTimer);

    if (typeof target === 'number') {
      this.list.scrollLeft = target;
    }

    this.isNavigating = false;
    this.pendingTarget = 0;
    this.syncCurrentIndex();
    this.updateNavState();
  }

  scrollToIndex(index) {
    const items = this.getItems();
    if (!items.length || index < 0 || index >= items.length) return;

    const target = this.getScrollPosition(index, items);

    this.isNavigating = true;
    this.currentIndex = index;
    this.pendingTarget = target;
    this.updateNavState();

    if (Math.abs(this.list.scrollLeft - target) < 1) {
      this.finishNavigation(target);
      return;
    }

    this.list.scrollTo({ left: target, behavior: 'smooth' });

    this.navTimer = window.setTimeout(() => {
      this.finishNavigation(this.pendingTarget);
    }, 500);
  }

  scrollByDirection(direction) {
    if (this.isNavigating) return;

    this.syncCurrentIndex();

    const items = this.getItems();
    if (!items.length) return;

    if (direction < 0) {
      const alignedPosition = this.getScrollPosition(this.currentIndex, items);

      if (this.list.scrollLeft > alignedPosition + 1) {
        this.scrollToIndex(this.currentIndex);
        return;
      }

      if (this.currentIndex <= 0) return;

      this.scrollToIndex(this.currentIndex - 1);
      return;
    }

    if (!this.canScrollNext(items)) return;

    this.scrollToIndex(this.currentIndex + 1);
  }
}

if (!customElements.get('customer-reviews-slider')) {
  customElements.define('customer-reviews-slider', CustomerReviewsSlider);
}
